import { useState, useEffect, useCallback, useMemo } from 'react';

import { useToast } from '@/components/Toast/context';
import LobbyPage from '@/app/pages/LobbyPage/LobbyPage';
import { Permissions } from '@/app/types';
import { useMeetingData, useUserData } from '@/app/queries/MeetingQuery/context';
import { formatDate } from '@/app/utils/date';
import { EVENTS, gtag } from '@/lib/gtag';


interface ILobbyPageProps {
  onJoin(permissions: Permissions): void;
}

export default function LobbyPageContainer({ onJoin }: ILobbyPageProps) {
  const toast = useToast();
  const [permissions, setPermissions] = useState({ audio: false, video: false });
  // False on first paint means "not settled yet", not "denied" — the warning
  // waits for the actual verdict so the resolved layout is the first one drawn.
  const [isMicrophoneDenied, setIsMicrophoneDenied] = useState(false);
  const [videoStream, setVideoStream] = useState<MediaStream | undefined>();

  const meetingData = useMeetingData();
  const { isClient } = useUserData();

  const lobbyPageData = useMemo(() => {
    const customer = meetingData.participants.find((participant) => !participant.is_host);
    const staff = meetingData.participants.find((participant) => participant.is_host);

    return {
      startTime: formatDate(meetingData.meeting_data.appointment_start),
      address: meetingData.meeting_data.house_address,
      customerName: customer?.user_name,
      staffName: staff?.user_name
    }
  }, [meetingData]);

  const requestPermissions = useCallback(async (permissions: Permissions) => {
    const stream = await navigator.mediaDevices.getUserMedia(permissions);

    setPermissions((prevState) => ({
      ...prevState,
      audio: permissions.audio ? Boolean(stream.getAudioTracks().length) : prevState.audio,
      video: permissions.video ? Boolean(stream.getVideoTracks().length) : prevState.video,
    }));

    if (permissions.video) {
      setVideoStream(stream);
    }

    return stream;
  }, [setPermissions]);

  const handleJoinCall = async () => {
    try {
      // the last permissions check before join
      const stream = await navigator.mediaDevices.getUserMedia(
        { video: false, audio: true }
      );
      stream.getTracks().forEach(track => track.stop());
    } catch (error) {
      console.debug(error);
      toast.publish({
        msg: 'Please check your microphone availability and refresh the page',
        type: 'Error'
      });
      return;
    }

    gtag(EVENTS.JOIN_CHANNEL, permissions);
    videoStream?.getTracks().forEach(track => track.stop());
    onJoin(permissions);
  };

  useEffect(function requestPermissionsOnMount() {
    requestPermissions({ audio: true }).catch(() => {
      setIsMicrophoneDenied(true);
      toast.publish({
        msg: 'Please grant microphone permissions and refresh the page',
        type: 'Error',
      });
    });

    requestPermissions({ video: true }).catch(() => {
      toast.publish({
        msg: 'Please grant camera permissions and refresh the page',
        type: 'Error',
      });
    });
  }, [requestPermissions, toast]);

  return (
    <LobbyPage
      {...lobbyPageData}
      permissions={permissions}
      isMicrophoneDenied={isMicrophoneDenied}
      requestPermissions={requestPermissions}
      videoStream={videoStream}
      onJoin={handleJoinCall}
      isClient={isClient}
    />
  );
}
